<script lang="ts">
	import { format } from 'svelte-i18n';
	import { Container } from '@Shelly/shelly-design-system';

	import { readDeviceStatus } from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';
	import { rooms } from 'src/stores/lists/rooms';
	import { onMount } from 'svelte';
	import { isWallDisplay, is_gen_2 } from '../../../../helpers/interface_helpers';
	import { getAddonStatus } from 'src/helpers/addons/addon_helpers';
	import { getExtSensorsSettings } from '../../../../helpers/ext_sensor_helpers';

	import { create_temperature_sensor } from '../../helpers/ui_to_temp_scheme';
	import { isXT1Module } from 'src/helpers/xt1_helpers/template_helpers';
	import { filterXT1Temperatures } from '../../helpers/virtual_temp_scheme_helpers';
	import { capFirstChar, _deepClone } from '../../../../helpers/utilities';
	import type { ITempSchemeObj } from '../../../../types/TemperatureSchemeType';
	import { translate_sensor_to_composed_ui_id } from '../../helpers/temp_scheme_to_ui';
	import SelectList from 'src/components/SelectList/SelectList.svelte';
	import type { Room } from 'src/components/SelectList/SelectList.types';
	import { defaultHiddenDevicesRoom } from 'src/helpers/constants';
	import EmptyDeviceTypeList from 'src/components/Lists/EmptyDeviceTypeList.svelte';
	import { isTestUser } from 'src/helpers/user_helpers';
	import { HiddenDevicesRoomID, SharedRecentlyDevicesRoomID } from 'src/helpers/service_rooms_helpers';
	import { sortRoomsWithMissingDevices } from 'src/components/Lists/list-helpers';
	import type { Device } from 'src/types/Device';

	export let temp_scheme_object: ITempSchemeObj;
	export let firstSelectedRef: HTMLElement = null;

	let sensors = [];
	let selectedItem = { id: undefined, value: undefined };
	$: selectedItem =
		temp_scheme_object.tschem?.r && sensors.length > 0
			? sensors.find(
					(el) => el.id === translate_sensor_to_composed_ui_id(temp_scheme_object.tschem.r),
				) || {
					id: undefined,
					value: undefined,
				}
			: { id: undefined, value: undefined };

	const generateSensorUIObject = (temp_scheme_object) => {
		return {
			name: translate_sensor_to_composed_ui_id(temp_scheme_object.tschem.r),
			id: translate_sensor_to_composed_ui_id(temp_scheme_object.tschem.r),
			roomID: 'missing_devices',
			selected: true,
			icon: 'text-warning fas fa-warning',
		};
	};

	//console.log('Selected item ', JSON.stringify(selectedItem));
	const filterDevicesByRoomID: any = (devices: any) => {
		//only devices from user account in rooms passed here
		let filteredDevicesByRoom = {};

		devices.forEach((device: any, i) => {
			const roomID = device.room_id;

			const allRooms = {
				...$rooms,
				...(isTestUser() && {
					[defaultHiddenDevicesRoom.value as number]: {
						name: $format(defaultHiddenDevicesRoom.title, {
							default: defaultHiddenDevicesRoom.translationDefault,
						}),
						id: defaultHiddenDevicesRoom.value,
					},
				}),
			};

			const deviceStatus = readDeviceStatus(device.id);
			const relayStatus = device.relay_id ? readDeviceStatus(device.relay_id) : undefined;
			const deviceData = {
				name: `${device.name}${
					!deviceStatus?.online && !relayStatus?.online
						? `- ${capFirstChar($format('label_offline'))}`
						: ''
				}`,
				selected:
					selectedItem?.id &&
					(selectedItem?.id === device.id || selectedItem?.id === device.relay_id),
				id: device.id,
				roomName: allRooms[roomID]?.name || 'Unknown room',
				roomID: roomID,
				image: device.image ? device.image : undefined,
				icon: device.icon ? device.icon : undefined,
				backgroundColor: device?.backgroundColor,
				ext_sen_id: device.ext_sen_id,
				is_virtual_temp: device.is_virtual_temp,
				is_ext_temp: device.is_ext_temp,
				hwID: device.hwID,
			};

			if (filteredDevicesByRoom[roomID]) {
				filteredDevicesByRoom[roomID] = {
					cards: [...filteredDevicesByRoom[roomID].cards, deviceData],
					roomName: allRooms[roomID].name || 'Unknown room',
					roomID: roomID,
				};
			} else {
				filteredDevicesByRoom[roomID] = {
					cards: [deviceData],
					roomName: allRooms[roomID]?.name || 'Unknown room',
					roomID: roomID,
				};
			}
		});

		if (selectedItem.id === undefined && devices.length && temp_scheme_object?.tschem?.r) {
			handle_sensor_change(generateSensorUIObject(temp_scheme_object));

			filteredDevicesByRoom['missing_devices'] = {
				cards: [generateSensorUIObject(temp_scheme_object)],
				roomName: $format('missing_devices_label', {
					default: 'Missing Devices',
				}),
				roomID: 'missing_devices',
			};
		}

		return sortRoomsWithMissingDevices(filteredDevicesByRoom);
	};

	const filterTemperatures = (devices) => {
		let filtered = [];
		devices.forEach((d) => {
			if (
				!d.shared &&
				(isTestUser() || d.room_id !== HiddenDevicesRoomID) &&
				d.room_id != `${SharedRecentlyDevicesRoomID}`
			) {
				// add 'sensor' devices as available sensors
				if (
					d.category === 'sensor' &&
					[
						'SHSEN-1',
						'SHMOS-02',
						'SHSM-01',
						'SHHT-1',
						'SHDW-2',
						'SHWT-1',
						'SNSN-0013A',
						'S3SN-0U12A',
						'S3SN-1U12A', //H&T G3 FFS
						'SBHT-003C', //blu ht
						'SBHT-103C', //blu ht display
						'SBHT-203C', //blu ht zb
						'SBWS-90CM', //Weather Station
					].includes(d.type)
				) {
					filtered.push(d);
				}
				// add gen.2 add-on sensors and bind them to the "first channel / main device"
				if (
					is_gen_2(d) &&
					(['inputs_reader', 'relay', 'roller', 'light', 'module'].includes(d.category) ||
						isXT1Module(d.type)) &&
					d.channel === 0
				) {
					if (isWallDisplay(d.type)) {
						filtered.push(d);
					}
					//filter all relays and inputs readers with external temperature sensors and add ids in a list for SelectionList
					let addOns = getAddonStatus(d);
					if (addOns.length > 0) {
						addOns.forEach((addOn) => {
							if (addOn.peripheralType === 'temperature') {
								const peripheralDevice = devices.find(
									(dev: Device) => dev.id === `${d.id}_${addOn.sensor.extid}`,
								);
								filtered.push({
									id: d.id + '|TMP|' + addOn.sensor.extid,
									room_id: peripheralDevice ? peripheralDevice.room_id : d.room_id,
									image: peripheralDevice ? peripheralDevice.image : d.image,
									icon: peripheralDevice ? peripheralDevice.icon : d.icon,
									relay_id: d.id,
									name:
										peripheralDevice?.name ||
										addOn.sensor.name + ' (' + d.name + ')',
									ext_sen_id: addOn.sensor.extid,
									is_ext_temp: true,
									hwID: addOn.extid,
								});
							}
						});
					}
				} else if (d.category === 'relay' && d.gen !== 2 && d.channel === 0) {
					//filter all relays with external temperature sensors and add ids in a list for SelectionList

					let t_sensors = getExtSensorsSettings(d, 'temperature');
					if (t_sensors.length > 0) {
						for (let i = 0; i < t_sensors.length; i++) {
							filtered.push({
								id: d.id + '|TMP|' + i,
								image: d.image,
								relay_id: d.id,
								room_id: d.room_id,
								name: t_sensors[i].name || d.name,
								ext_sen_id: t_sensors[i].extid,
								is_ext_temp: true,
								hwID: t_sensors[i]?.hwID,
							});
						}
					}
				}
			}
			filtered = filterXT1Temperatures(d, filtered);
		});
		return filtered;
	};

	let roomsFiltered: Room[];
	$: roomsFiltered = filterDevicesByRoomID(sensors);

	const handle_sensor_change = (device) => {
		let sensor = create_temperature_sensor(device);
		// let copy_scheme: ITempSchemeObj = _deepClone(
		// 	$temp_scheme_object
		// ) as ITempSchemeObj;

		//TODO: drop it when shelly-app-2 is deprecated
		let sensor_ind = temp_scheme_object?._meta?.data?.findIndex((el) => el.sensor_id !== undefined);
		if (temp_scheme_object?._meta?.data?.length >= 0) {
			temp_scheme_object._meta.data[sensor_ind] = {
				sensor_id: ['relay', 'inputs_reader', 'roller'].includes(device.category)
					? device.relay_id
					: device.id,
			};
		} else {
			temp_scheme_object._meta['data'] = [];
			temp_scheme_object._meta['data'].push({
				sensor_id: ['relay', 'inputs_reader', 'roller'].includes(device.category)
					? device.relay_id
					: device.id,
			});
		}

		//TODO: drop it when shelly-app-2 deprecated
		temp_scheme_object._meta['ext_sensor'] = device.category === 'relay';
		temp_scheme_object._meta['ext_ind'] = '' + device.ext_sen_id;
		temp_scheme_object._meta['hw_id'] = '' + device.hwID;

		//change sensor and update after all meta data
		temp_scheme_object.tschem = { ...sensor.tschem };

		// temp_scheme_object.change(copy_scheme);
	};

	const mapRoomDevicesSelectedProp = (room, deviceSelected) => {
		const updatedCards = room.cards.map((device) => {
			if (device.id === deviceSelected?.id) {
				return {
					...device,
					selected: true,
				};
			}
			return {
				...device,
				selected: false,
			};
		});

		return {
			...room,
			cards: updatedCards,
		};
	};

	const handleSelectItem = (device) => {
		selectedItem = {
			...device,
		};

		roomsFiltered = roomsFiltered.map((room) => {
			return mapRoomDevicesSelectedProp(room, device);
		});
	};

	onMount(() => {
		sensors = [...filterTemperatures(Object.values($devices))];
	});
</script>

<Container padding="scrollPaddings">
	{#if roomsFiltered.length > 0}
		<!-- DO NOT REMOVE COMMENT: Padding references -->
		<div class="pt-s" />
		<SelectList
			missingDevicesAlertHeading={$format('missing_devices_alert_label ', {
				values: {
					type: $format('device_type_thermostat', { default: 'thermostat' }),
				},
				default: 'You have missing device(s) in this {type}.',
			})}
			title={$format('select_thermostat_sensor', { default: 'Select sensor' })}
			list={roomsFiltered}
			variant="radio"
			selectedCardsId={[
				{
					id:
						selectedItem.id ||
						(temp_scheme_object?.tschem?.r &&
							translate_sensor_to_composed_ui_id(temp_scheme_object.tschem.r)),
				},
			]}
			on:click={(e) => {
				handleSelectItem(e.detail);
				handle_sensor_change(e.detail);
			}}
			bind:firstSelectedRef
		/>
		<!-- DO NOT REMOVE COMMENT: Padding references -->
		<div class="pb-s" />
	{:else}
		<EmptyDeviceTypeList type="modal" withoutMargin />
	{/if}
</Container>
